// src/components/AccountPanel.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/**
 * Sign in and registration, in the right pane.
 *
 * ROLE-AWARE BY CONSTRUCTION. The form does not know which credentials a role
 * needs — it asks the server (`/api/account/schema`) and renders that. The same
 * rules validate the submission, so the form and the validator cannot drift
 * apart and start disagreeing about what is required.
 *
 * That matters because the roles are genuinely unequal: an architect must give a
 * Council of Architecture number, an interior designer has no statutory body to
 * give one from, and a client has nothing but a verified phone and email. A form
 * that demanded a number from all three would collect invented ones.
 *
 * Nothing here blocks the 3D view. Someone drawing a site plan has no reason to
 * have an account yet; this opens when they need one.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
  ok: 'var(--ok)',
  err: 'var(--err)',
};

const CRED_LABEL = {
  coa: 'Council of Architecture no.',
  rera: 'RERA registration no.',
  gstin: 'GSTIN',
  pwd: 'PWD contractor registration',
  municipal: 'Municipal licence no.',
  iiid: 'IIID membership no.',
  iei: 'IE(I) membership no.',
  cin: 'Company CIN',
  udyam: 'Udyam / MSME no.',
  degree: 'Qualification (degree, institution, year)',
};

const CRED_HINT = {
  coa: 'CA/YYYY/NNNNN, as printed on the certificate',
  rera: 'as issued by the state authority',
  gstin: '15 characters, e.g. 27ABCDE1234F1Z5',
  cin: '21 characters, e.g. U45200MH2015PTC123456',
  udyam: 'UDYAM-XX-00-0000000',
  degree: 'e.g. B.Arch, CEPT, 2014',
};

const STATES = [
  'Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Odisha', 'Punjab', 'Rajasthan',
  'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

/** The session token lives in memory for the tab's lifetime, and in
 *  sessionStorage so a reload does not sign the user out. sessionStorage rather
 *  than localStorage: it dies with the tab, which shortens the window in which a
 *  stolen token is useful. */
const TOKEN_KEY = 'decon.session';
export const readToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
};
/** The display name, kept beside the token so the header can greet someone on a
 *  reload without waiting for /me — and without this panel being mounted, which
 *  it usually is not. Cleared with the token, never outliving the session. */
const NAME_KEY = 'decon.name';
export const readName = () => {
  try { return sessionStorage.getItem(NAME_KEY) || null; } catch { return null; }
};
const writeName = (n) => {
  try { n ? sessionStorage.setItem(NAME_KEY, n) : sessionStorage.removeItem(NAME_KEY); }
  catch { /* private mode */ }
};

/** The one signal for "who is signed in", for anything outside this panel:
 *  the header button, the project list, the open project. Carries the name so
 *  the header does not have to make a request of its own to say hello. */
const announceAuth = (signedIn, name = null) => {
  try {
    window.dispatchEvent(new CustomEvent('decon:auth', { detail: { signedIn, name } }));
  } catch { /* non-browser test environment */ }
};

const writeToken = (t, name) => {
  try { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); }
  catch { /* private mode — the session simply will not survive a reload */ }
  writeName(t ? name || readName() : null);
  // Projects are per-account now, so the list on screen belongs to whoever was
  // signed in a moment ago. Announce the change rather than leaving a stale list
  // that the next person can read.
  announceAuth(!!t, t ? readName() : null);
};

/**
 * Drop a session the server has already rejected.
 *
 * A 401 from any API call is the authoritative word that the token is dead —
 * and it can arrive while this panel is not even mounted, since it only renders
 * when the Login pane is open. Without this the header would keep saying
 * "Logged in" next to an empty project list.
 *
 * The readToken guard is what stops a loop: clearing announces, listeners
 * refetch, that 401s again, and the second pass finds no token to clear.
 */
export const clearSession = () => { if (readToken()) writeToken(null); };

/** What to call a field the server refused, in the summary above the button. */
function errorFieldName(key) {
  const names = {
    displayName: 'Name', email: 'Email', phone: 'Mobile', password: 'Password',
    city: 'City', state: 'State', role: 'I am a',
    'experience.years': 'Years in the field', 'experience.projectsBand': 'Projects completed',
    'experience.projectTypes': 'Project types', 'credential.rera.issuer': 'RERA issuing state',
  };
  if (names[key]) return names[key];
  const m = /^credential\.(.+)$/.exec(key);
  return m ? (CRED_LABEL[m[1]] || m[1]) : key;
}

export default function AccountPanel({ apiBase }) {
  const [schema, setSchema] = useState(null);
  const [mode, setMode] = useState('signin');      // 'signin' | 'register'
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  // Off by default, and off again on every mount: a password left showing is a
  // password read by whoever is stood behind you.
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    role: 'architect', accountType: 'individual',
    displayName: '', firmName: '', email: '', phone: '', password: '',
    city: '', state: '', bio: '', messagePrivacy: 'connections',
    credentials: {}, credIssuer: {},
    years: '', projectsBand: '', largestValue: '', projectTypes: [], cities: '',
  });

  const rootRef = useRef(null);
  // Counts REFUSED submits. The jump-to-first-problem effect keys on this and
  // nothing else, so it fires once per press of the button.
  const [refusedAt, setRefusedAt] = useState(0);
  // Changing a field clears that field's error, so the summary only ever lists
  // what is still wrong.
  const ERROR_KEYS = { years: ['experience.years'], projectsBand: ['experience.projectsBand'], projectTypes: ['experience.projectTypes'] };
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => {
      const keys = k === 'credentials' || k === 'credIssuer'
        ? Object.keys(er).filter((x) => x.startsWith('credential.'))
        : (ERROR_KEYS[k] || [k]);
      if (!keys.some((x) => x in er)) return er;
      const next = { ...er };
      keys.forEach((x) => delete next[x]);
      return next;
    });
  };

  useEffect(() => {
    fetch(`${apiBase}/api/account/schema`)
      .then((r) => r.json())
      .then((d) => { if (d.ok) setSchema(d); })
      .catch(() => setNotice('Could not reach the server. Is the backend running?'));
  }, [apiBase]);

  // restore a session on load
  useEffect(() => {
    const tok = readToken();
    if (!tok) return;
    fetch(`${apiBase}/api/account/me`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => (r.ok ? r.json() : null))
      // Confirm out loud that the stored token is a live session. Without this
      // the header would be trusting sessionStorage alone, and a token the
      // server has already revoked would still read as "Logged in".
      .then((d) => { if (d?.ok) { setMe(d.user); writeName(d.user.displayName); announceAuth(true, d.user.displayName); } else writeToken(null); })
      .catch(() => {});
  }, [apiBase]);

  const role = useMemo(
    () => (schema?.roles || []).find((r) => r.key === form.role) || null,
    [schema, form.role]
  );

  // A refused form used to look like nothing happened: the error sat on a field
  // scrolled out of view (typically Mobile, far above the button). Go to it.
  //
  // Keyed on the refusal COUNT, never on `errors`. Keyed on errors, this ran
  // again every time typing cleared one — scrolling away and stealing the caret
  // to the next bad field while the person was still typing in this one.
  useEffect(() => {
    if (!refusedAt || !rootRef.current) return;
    const el = rootRef.current.querySelector('[data-has-error="true"]');
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    const input = el.querySelector('input, select, textarea');
    if (input) input.focus({ preventScroll: true });
  }, [refusedAt]);

  const submit = useCallback(async () => {
    setBusy(true); setErrors({}); setNotice(null);
    const isRegister = mode === 'register';
    const body = isRegister ? {
      role: form.role,
      accountType: form.accountType,
      displayName: form.displayName,
      firmName: form.firmName,
      email: form.email,
      phone: form.phone,
      password: form.password,
      city: form.city,
      state: form.state,
      bio: form.bio,
      messagePrivacy: form.messagePrivacy,
      credentials: Object.entries(form.credentials)
        .filter(([, v]) => String(v || '').trim())
        .map(([kind, value]) => ({ kind, value, issuer: form.credIssuer[kind] || null })),
      experience: {
        years: form.years, projectsBand: form.projectsBand,
        largestValue: form.largestValue, projectTypes: form.projectTypes,
        cities: form.cities.split(',').map((c) => c.trim()).filter(Boolean),
      },
    } : { email: form.email, password: form.password };

    try {
      const res = await fetch(`${apiBase}/api/account/${isRegister ? 'register' : 'login'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        if (d.errors) { setErrors(d.errors); setRefusedAt((n) => n + 1); }
        if (d.error) setNotice(d.error);
        return;
      }
      writeToken(d.token, d.user?.displayName);
      setMe(d.user);
      setForm((f) => ({ ...f, password: '' }));
    } catch (e) {
      setNotice(`Could not reach the server — ${e.message}`);
    } finally { setBusy(false); }
  }, [apiBase, mode, form]);

  const signOut = useCallback(async () => {
    const tok = readToken();
    writeToken(null); setMe(null);
    if (tok) {
      // revoke server-side too, so a copied token stops working
      fetch(`${apiBase}/api/account/session`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tok}` },
      }).catch(() => {});
    }
  }, [apiBase]);

  // ---- signed in ----------------------------------------------------------
  if (me) {
    return (
      <div style={S.wrap}>
        <div style={S.head}>Signed in</div>
        <div style={S.card}>
          <div style={S.name}>{me.displayName}</div>
          <div style={S.sub}>
            {(schema?.roles || []).find((r) => r.key === me.role)?.label || me.role}
            {me.city ? ` · ${me.city}` : ''}{me.state ? `, ${me.state}` : ''}
          </div>
          {!!me.credentials?.length && (
            <div style={S.credList}>
              {me.credentials.map((c, i) => (
                <div key={i} style={S.credRow}>
                  <span style={S.credKind}>{CRED_LABEL[c.kind] || c.kind}</span>
                  <span style={S.credVal}>{c.value}</span>
                  <span style={{ ...S.tier, ...tierStyle(c.tier) }}>{c.tier}</span>
                </div>
              ))}
            </div>
          )}
          <div style={S.hint}>
            Credentials are self-declared. <b>Checked</b> means the format is
            right, not that a register has confirmed it.
          </div>
        </div>

        <VerifyBlock apiBase={apiBase} me={me} onVerified={setMe} />

        <button style={S.ghost} onClick={signOut}>Sign out</button>
      </div>
    );
  }

  // ---- signed out ---------------------------------------------------------
  const isRegister = mode === 'register';
  return (
    <div style={S.wrap} ref={rootRef}>
      <div style={S.tabs}>
        <button style={{ ...S.tab, ...(!isRegister ? S.tabOn : null) }}
                onClick={() => { setMode('signin'); setErrors({}); }}>Sign in</button>
        <button style={{ ...S.tab, ...(isRegister ? S.tabOn : null) }}
                onClick={() => { setMode('register'); setErrors({}); }}>Create account</button>
      </div>

      {notice && <div style={S.notice}>{notice}</div>}

      {/* Said before the form, not after it: nobody should fill in all of this
          to learn sign-up was full. An invited email still gets through, so the
          form stays. */}
      {isRegister && schema?.signup && !schema.signup.open && (
        <div style={S.notice} role="status">
          {schema.signup.full
            ? 'Registration is full for this test. If you were invited, you can still sign up with the email you were invited on.'
            : 'Sign-up is by invitation while VAUOM is in testing. Use the email you were invited on.'}
        </div>
      )}

      {isRegister && (
        <>
          <Field label="I am a" error={errors.role}>
            <select style={S.input} value={form.role} onChange={(e) => set('role', e.target.value)}>
              {(schema?.roles || []).map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Working as">
            <div style={S.row}>
              {['individual', 'firm'].map((t) => (
                <button key={t} type="button"
                        style={{ ...S.chip, ...(form.accountType === t ? S.chipOn : null) }}
                        onClick={() => set('accountType', t)}>
                  {t === 'individual' ? 'An individual' : 'A firm'}
                </button>
              ))}
            </div>
          </Field>

          <Field label={form.accountType === 'firm' ? 'Firm name' : 'Your name'} error={errors.displayName}>
            <input style={S.input} value={form.displayName}
                   onChange={(e) => set('displayName', e.target.value)}
                   placeholder="Shown in the directory" />
          </Field>
        </>
      )}

      <Field label="Email" error={errors.email}>
        <input style={S.input} type="email" autoComplete="email" value={form.email}
               onChange={(e) => set('email', e.target.value)} />
      </Field>

      {isRegister && (
        <Field label="Mobile" error={errors.phone} hint="Verified later. For a client, this is the identity.">
          <input style={S.input} inputMode="numeric" value={form.phone}
                 onChange={(e) => set('phone', e.target.value)} placeholder="10 digits" />
        </Field>
      )}

      <Field label="Password" error={errors.password}
             hint={isRegister ? 'At least 10 characters — a phrase only you would write.' : null}>
        <div style={S.pwWrap}>
          <input style={{ ...S.input, ...S.pwInput }} type={showPassword ? 'text' : 'password'}
                 value={form.password}
                 autoComplete={isRegister ? 'new-password' : 'current-password'}
                 onChange={(e) => set('password', e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && !isRegister) submit(); }} />
          {/* A word, not an eye: "Show"/"Hide" says both what pressing it does and
              which state the field is in, where a crossed-out eye leaves people
              guessing. aria-pressed carries that state to a screen reader, and
              tabIndex -1 keeps Tab running straight from the password to the
              button that submits. */}
          <button type="button" style={S.pwToggle} tabIndex={-1}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}>
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
      </Field>

      {isRegister && (
        <>
          <div style={S.row}>
            <Field label="City" error={errors.city} grow>
              <input style={S.input} value={form.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            <Field label="State" error={errors.state} grow>
              <select style={S.input} value={form.state} onChange={(e) => set('state', e.target.value)}>
                <option value="">Choose…</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          {/* Credentials — driven entirely by the role's rules */}
          {role && (role.required.length > 0 || role.optional.length > 0) && (
            <div style={S.section}>
              <div style={S.sectionHead}>
                Credentials
                <span style={S.sectionSub}>what other stakeholders check before connecting</span>
              </div>
              {role.required.map((kind) => (
                <Field key={kind} label={CRED_LABEL[kind] || kind} required
                       hint={CRED_HINT[kind]} error={errors[`credential.${kind}`]}>
                  <input style={S.input}
                         value={form.credentials[kind] || ''}
                         onChange={(e) => set('credentials', { ...form.credentials, [kind]: e.target.value })} />
                  {kind === 'rera' && (
                    <select style={{ ...S.input, marginTop: 6 }}
                            value={form.credIssuer.rera || ''}
                            onChange={(e) => set('credIssuer', { ...form.credIssuer, rera: e.target.value })}>
                      <option value="">Issuing state…</option>
                      {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </Field>
              ))}
              {role.optional.map((kind) => (
                <Field key={kind} label={CRED_LABEL[kind] || kind} hint={CRED_HINT[kind]}
                       error={errors[`credential.${kind}`]} optional>
                  <input style={S.input}
                         value={form.credentials[kind] || ''}
                         onChange={(e) => set('credentials', { ...form.credentials, [kind]: e.target.value })} />
                </Field>
              ))}
            </div>
          )}

          {role?.identityIsCredential && (
            <div style={S.hint}>
              There is no statutory register for this role, so your verified
              email and mobile are your identity here.
            </div>
          )}

          {/* Field experience */}
          {role?.experienceRequired && (
            <div style={S.section}>
              <div style={S.sectionHead}>
                Field experience
                <span style={S.sectionSub}>self-declared — what separates two people with the same registration</span>
              </div>
              <div style={S.row}>
                <Field label="Years in the field" error={errors['experience.years']} grow>
                  <input style={S.input} inputMode="numeric" value={form.years}
                         onChange={(e) => set('years', e.target.value)} />
                </Field>
                <Field label="Projects completed" error={errors['experience.projectsBand']} grow>
                  <select style={S.input} value={form.projectsBand}
                          onChange={(e) => set('projectsBand', e.target.value)}>
                    <option value="">Choose…</option>
                    {(schema?.projectBands || []).map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>

              <Field label={`Largest project — ${role.largestUnit || ''}`}>
                <input style={S.input} inputMode="decimal" value={form.largestValue}
                       onChange={(e) => set('largestValue', e.target.value)} />
              </Field>

              <Field label="Project types" error={errors['experience.projectTypes']}>
                <div style={S.wrapRow}>
                  {(schema?.projectTypes || []).map((t) => {
                    const on = form.projectTypes.includes(t);
                    return (
                      <button key={t} type="button"
                              style={{ ...S.chip, ...(on ? S.chipOn : null) }}
                              onClick={() => set('projectTypes',
                                on ? form.projectTypes.filter((x) => x !== t) : [...form.projectTypes, t])}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Cities worked in" hint="Comma separated. These relationships are local.">
                <input style={S.input} value={form.cities}
                       onChange={(e) => set('cities', e.target.value)} placeholder="Mumbai, Vapi" />
              </Field>
            </div>
          )}

          <Field label="Who can message you">
            <div style={S.row}>
              {[['connections', 'My connections'], ['anyone', 'Anyone registered']].map(([v, l]) => (
                <button key={v} type="button"
                        style={{ ...S.chip, ...(form.messagePrivacy === v ? S.chipOn : null) }}
                        onClick={() => set('messagePrivacy', v)}>{l}</button>
              ))}
            </div>
          </Field>
        </>
      )}

      {Object.keys(errors).length > 0 && (
        <div style={S.notice} role="alert">
          Please fix {Object.keys(errors).length === 1 ? 'this field' : 'these fields'}:{' '}
          {Object.keys(errors).map(errorFieldName).join(', ')}.
        </div>
      )}

      <button style={{ ...S.primary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
        {busy ? 'Working…' : isRegister ? 'Create account' : 'Sign in'}
      </button>

      {/* Said plainly rather than offered as a link. There is no email provider
          wired in (see backend/notify.js), so a "Forgot password?" link would
          promise a message nobody sends. The reset is done on the server with
          scripts/resetPassword.js while the test group is small. */}
      {!isRegister && (
        <div style={S.hint}>
          Forgot your password? There is no automatic reset yet — ask the VAUOM
          team and they will set a new one for you.
        </div>
      )}

      {isRegister && (
        <div style={S.hint}>
          We ask only what another stakeholder needs to decide whether to work
          with you. No address, no ID documents, no uploads.
        </div>
      )}
    </div>
  );
}

/**
 * Confirm control of an email address and a phone number.
 *
 * Kept to two rows that say plainly what state each channel is in, because the
 * state is the whole message: an unverified contact is one another stakeholder
 * cannot rely on, and for a Client — who has no register behind them — it is the
 * only identity this product can show.
 */
function VerifyBlock({ apiBase, me, onVerified }) {
  const [open, setOpen] = useState(null);        // 'email' | 'phone' | null
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = async (path, body) => {
    const tok = readToken();
    const res = await fetch(`${apiBase}/api/account/verify/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify(body),
    });
    return { res, d: await res.json().catch(() => ({})) };
  };

  const start = async (channel) => {
    setBusy(true); setMsg(null); setCode('');
    try {
      const { res, d } = await call('start', { channel });
      if (!res.ok) { setMsg(d.error || 'Could not send a code.'); return; }
      if (d.alreadyVerified) { setMsg('Already verified.'); return; }
      setOpen(channel);
      // devCode only ever arrives when the backend is running without a mail or
      // SMS provider; in that mode the code is also on the server console
      setMsg(d.devCode
        ? `No provider configured — your code is ${d.devCode}`
        : `Code sent to ${d.sentTo}.${d.delivered ? '' : ' Check the server console.'}`);
    } catch (e) { setMsg(`Could not reach the server — ${e.message}`); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { res, d } = await call('confirm', { channel: open, code });
      if (!res.ok) { setMsg(d.error || 'Could not verify that code.'); return; }
      onVerified(d.user); setOpen(null); setCode(''); setMsg(null);
    } catch (e) { setMsg(`Could not reach the server — ${e.message}`); }
    finally { setBusy(false); }
  };

  const Row = ({ channel, label, value, done }) => (
    <div style={S.credRow}>
      <span style={S.credKind}>{label}</span>
      <span style={S.credVal}>{value || '—'}</span>
      {done
        ? <span style={{ ...S.tier, ...tierStyle('verified') }}>verified</span>
        : (
          <button type="button" style={S.ghost} disabled={busy || !value}
                  onClick={() => start(channel)}>
            {open === channel ? 'Resend' : 'Verify'}
          </button>
        )}
    </div>
  );

  return (
    <div style={S.card}>
      <div style={S.sub}>Contact</div>
      <Row channel="email" label="Email" value={me.email} done={me.verified?.email} />
      <Row channel="phone" label="Mobile" value={me.phone} done={me.verified?.phone} />

      {open && (
        <div style={S.row}>
          <input style={S.input} value={code} inputMode="numeric" maxLength={6}
                 placeholder="6-digit code" autoComplete="one-time-code"
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          <button type="button" style={S.primary} disabled={busy || code.length !== 6}
                  onClick={confirm}>Confirm</button>
        </div>
      )}
      {msg && <div style={S.hint}>{msg}</div>}
      <div style={S.hint}>
        Verifying proves you can read the inbox or answer the number. It is what
        lets another stakeholder reach you — it does not confirm your credentials.
      </div>
    </div>
  );
}

function Field({ label, hint, error, required, optional, grow, children }) {
  return (
    <div style={{ ...S.field, ...(grow ? { flex: 1, minWidth: 0 } : null) }} data-has-error={error ? 'true' : undefined}>
      <label style={S.label}>
        {label}
        {required && <span style={S.req}>required</span>}
        {optional && <span style={S.opt}>optional</span>}
      </label>
      {children}
      {hint && !error && <div style={S.fieldHint}>{hint}</div>}
      {error && <div style={S.fieldErr}>{error}</div>}
    </div>
  );
}

function tierStyle(tier) {
  if (tier === 'verified') return { background: 'color-mix(in srgb, var(--ok) 18%, transparent)', color: 'var(--ok)' };
  if (tier === 'checked') return { background: 'color-mix(in srgb, var(--warn) 18%, transparent)', color: 'var(--warn)' };
  return { background: 'var(--surface-2)', color: T.dim };
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12, padding: 14, maxWidth: 460 },
  head: { fontSize: 12.5, fontWeight: 600, color: T.text },
  tabs: { display: 'flex', gap: 6 },
  tab: { flex: 1, fontSize: 12, fontWeight: 600, padding: '7px 10px', borderRadius: 8,
         cursor: 'pointer', border: '1px solid var(--btn-edge,#56657a)', background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)' },
  tabOn: { background: T.surface, color: T.text, border: `1px solid ${T.accent}` },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: T.text, display: 'flex',
           alignItems: 'center', gap: 6 },
  req: { fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
         color: 'var(--ok)' },
  opt: { fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: T.dim },
  // border-box: without it 100% width PLUS padding and border overflowed the
  // column, and the State select in the City | State row spilled over City.
  input: { width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: '7px 9px', borderRadius: 7,
           border: `1px solid ${T.border}`, background: 'var(--surface-3)',
           color: T.text, outline: 'none' },
  row: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  pwWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  pwInput: { paddingRight: 58 },   // room for the button, so text never runs under it
  pwToggle: {
    position: 'absolute', right: 5, fontSize: 11, fontWeight: 600,
    padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
    border: '1px solid var(--btn-edge,#56657a)',
    background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)',
  },
  wrapRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: { fontSize: 11, padding: '5px 10px', borderRadius: 14, cursor: 'pointer',
          border: '1px solid var(--btn-edge,#56657a)', background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)',
          textTransform: 'capitalize' },
  chipOn: { border: `1px solid ${T.accent}`, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: T.text },
  section: { display: 'flex', flexDirection: 'column', gap: 10, padding: 11,
             border: `1px solid ${T.border}`, borderRadius: 9, background: T.surface },
  sectionHead: { fontSize: 11.5, fontWeight: 600, color: T.text, display: 'flex',
                 flexDirection: 'column', gap: 2 },
  sectionSub: { fontSize: 10, fontWeight: 400, color: T.dim },
  primary: { fontSize: 12.5, fontWeight: 600, padding: '9px 14px', borderRadius: 8,
             cursor: 'pointer', border: `1px solid ${T.accent}`, background: T.accent, color: '#fff' },
  ghost: { fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 8,
           cursor: 'pointer', border: '1px solid var(--btn-edge,#56657a)', background: 'var(--btn-face,#313d4b)', color: T.text },
  card: { border: `1px solid ${T.border}`, borderRadius: 9, padding: 12,
          background: T.surface, display: 'flex', flexDirection: 'column', gap: 8 },
  name: { fontSize: 15, fontWeight: 600, color: T.text },
  sub: { fontSize: 11.5, color: T.dim },
  credList: { display: 'flex', flexDirection: 'column', gap: 5 },
  credRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, flexWrap: 'wrap' },
  credKind: { color: T.dim, minWidth: 150 },
  credVal: { color: T.text, fontFamily: 'ui-monospace, Menlo, monospace' },
  tier: { fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
          padding: '2px 7px', borderRadius: 10 },
  fieldHint: { fontSize: 10, color: T.dim, lineHeight: 1.45 },
  fieldErr: { fontSize: 10.5, color: T.err, lineHeight: 1.45 },
  hint: { fontSize: 10.5, color: T.dim, lineHeight: 1.5 },
  notice: { fontSize: 11.5, color: T.text, background: 'color-mix(in srgb, var(--err) 12%, transparent)',
            border: `1px solid ${T.err}`, borderRadius: 7, padding: '7px 9px' },
};
